import { format, isValid, parseISO } from "date-fns";
import type { inboxOrderDetailDataSchema } from "@go-novus/admin-api";

import * as m from "../../paraglide/messages.js";
import { formatMoney } from "./order-display";
import type {
  WidgetOrderGroup,
  WidgetOrderItem,
  WidgetOrderCard,
  WidgetOrderTracking,
} from "./order-card";

type Detail = import("zod").z.infer<typeof inboxOrderDetailDataSchema>;
const sourceTime = (
  value: string | null | undefined,
  formatDateTime?: (iso: string) => string,
) => {
  const parsed = value ? parseISO(value) : null;
  return parsed && isValid(parsed)
    ? formatDateTime
      ? formatDateTime(value ?? "") || m.widget_order_unknown()
      : format(parsed, "PPpp")
    : m.widget_order_unknown();
};
/** 源枚举转为可读文字，未知新枚举不推断运输进度。 */
const status = (value: string | null) =>
  value === "LABEL_PRINTED" || value === "LABEL_PURCHASED"
    ? m.widget_order_label_created()
    : value
      ? value.replaceAll("_", " ").toLowerCase()
      : m.widget_order_unknown();

/** 顾客与只读 Inbox 共用；所有动作由调用方注入，不在渲染时读取订单。 */
export function shippingOrderView(
  payload: Detail,
  interactive = true,
  formatDateTime?: (iso: string) => string,
): Pick<WidgetOrderCard, "groups" | "adjustments"> {
  const time = (value: string | null | undefined) =>
    sourceTime(value, formatDateTime);
  const groups: WidgetOrderGroup[] = [];
  const makeItem = (
    item: Detail["items"][number],
    quantity: number,
  ): WidgetOrderItem => ({
    title: item.title,
    imageUrl: item.image_url,
    detail: [item.variant, m.widget_order_item_quantity({ count: quantity })]
      .filter(Boolean)
      .join(" · "),
    price: "",
  });
  // 整单取消且没有成功的履约记录：商品统一进「Canceled」组（设计稿 2.5 已取消）。
  // 有履约记录时取消前可能已发出一部分，数量未知，仍按原规则不推断。
  const canceledUnsent =
    !!payload.shipping?.cancelled_at &&
    !payload.shipping.fulfillments.some((f) => f.status === "SUCCESS");
  const definitions = canceledUnsent
    ? [
        {
          label: m.widget_order_canceled_items(),
          icon: "canceled" as const,
          quantity: (item: Detail["items"][number]) => item.quantity,
        },
      ]
    : [
        {
          label: m.widget_order_shipped_items(),
          icon: "shipped" as const,
          quantity: (item: Detail["items"][number]) =>
            item.shipped_quantity ?? 0,
        },
        {
          label: m.widget_order_fulfilled_items(),
          icon: "shipped" as const,
          quantity: (item: Detail["items"][number]) =>
            (item.fulfilled_quantity ?? 0) - (item.shipped_quantity ?? 0),
        },
        {
          label: m.widget_order_pending_items(),
          icon: "preparing" as const,
          quantity: (item: Detail["items"][number]) =>
            item.pending_quantity ?? 0,
        },
        {
          label: m.widget_order_no_shipping(),
          icon: "mail" as const,
          quantity: (item: Detail["items"][number]) =>
            item.requires_shipping === false ? item.quantity : 0,
        },
        {
          label: m.widget_order_shipping_unknown(),
          icon: "package" as const,
          quantity: (item: Detail["items"][number]) =>
            item.requires_shipping !== false &&
            item.fulfilled_quantity === undefined
              ? item.quantity
              : 0,
        },
      ];
  for (const definition of definitions) {
    const items = payload.items.flatMap((item) => {
      const quantity = definition.quantity(item);
      return quantity > 0 ? [makeItem(item, quantity)] : [];
    });
    if (items.length)
      groups.push({ label: definition.label, icon: definition.icon, items });
  }
  const shipping = payload.shipping;
  // 物流块按设计稿 2.5 写：承运商、最新进展和它的时间、单号、预计送达。履约记录不另起一行，
  // 也不再加「只显示已知信息」「已送达仍可求助」这类说明（GON-352）；没列全的记录仍然写明还有几条。
  const trackings = (shipping?.fulfillments ?? []).flatMap(
    (f): WidgetOrderTracking[] => {
      const latest = [
        f.status && f.status !== "SUCCESS" ? status(f.status) : undefined,
        status(f.latest_event?.status ?? f.latest_status),
      ]
        .filter(Boolean)
        .join(" · ");
      const updatedAt = f.latest_event
        ? time(f.latest_event.happened_at)
        : undefined;
      const estimate = f.eta
        ? m.widget_order_eta_source({ time: time(f.eta) })
        : m.widget_order_eta_unknown();
      if (f.trackings.length === 0)
        return f.requires_shipping === false
          ? []
          : [
              {
                carrier: m.widget_order_carrier_unknown(),
                number: "—",
                latest,
                updatedAt,
                estimate,
              },
            ];
      return f.trackings.map((t) => ({
        carrier: t.carrier ?? m.widget_order_carrier_unknown(),
        number: t.number ?? "—",
        latest,
        updatedAt,
        estimate,
        url: interactive ? t.url : undefined,
      }));
    },
  );
  if (trackings.length) {
    // 卡片把各组的物流块合在一起画在内容区最上面，挂在哪一组只影响数据，不影响位置。
    const host = groups.find((group) => group.icon === "shipped") ?? groups[0];
    if (host) host.trackings = trackings;
    else groups.push({ label: "", icon: "package", items: [], trackings });
  }
  const moreTrackings = (shipping?.fulfillments ?? []).reduce(
    (sum, f) => sum + f.remaining_trackings,
    0,
  );
  const notes = [
    payload.remaining_items > 0
      ? m.widget_orders_more_items({ count: payload.remaining_items })
      : undefined,
    shipping?.remaining_fulfillments
      ? m.widget_order_more_fulfillments({
          count: shipping.remaining_fulfillments,
        })
      : undefined,
    moreTrackings > 0
      ? m.widget_order_more_trackings({ count: moreTrackings })
      : undefined,
  ].filter(Boolean);
  for (const note of notes)
    groups.push({ label: "", icon: "package", items: [], note });
  const lines = [
    ...(shipping?.cancelled_at
      ? [
          {
            label: m.widget_order_cancellation(),
            value: time(shipping.cancelled_at),
          },
        ]
      : []),
    ...(shipping?.refunds ?? []).map((r) => ({
      label: m.widget_order_refund_record(),
      value: formatMoney(r.amount, r.currency),
      detail: time(r.created_at),
    })),
    ...(shipping?.returns ?? []).map((r) => ({
      label: m.widget_order_return_record({
        name: r.name ?? m.widget_order_unknown(),
      }),
      value: status(r.status),
      detail: time(r.created_at),
    })),
    ...(shipping?.remaining_refunds
      ? [
          {
            label: m.widget_order_more_refunds({
              count: shipping.remaining_refunds,
            }),
            value: "",
          },
        ]
      : []),
    ...(shipping?.remaining_returns
      ? [
          {
            label: m.widget_order_more_returns({
              count: shipping.remaining_returns,
            }),
            value: "",
          },
        ]
      : []),
  ];
  return {
    groups,
    ...(lines.length
      ? { adjustments: { title: m.widget_order_adjustments(), lines } }
      : {}),
  };
}
